import { API_KEY_DEFAULT_EXPIRY_DAYS, API_KEY_MAX_EXPIRY_DAYS } from '@atlas/types'
import { Button, DialogContent, DialogTitle, HelpText, Input, Label } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'

export interface ApiKeyCreateDialogProps {
	open: boolean
	submitting?: boolean
	onCreate: (input: { name: string; expiresInDays: number }) => void
	onCancel: () => void
}

/**
 * Create-a-key form.
 *
 * The lifetime is in days, matching the request schema — which caps it rather than
 * clamping it, so a value over the maximum is refused. The input carries the same
 * bound so the refusal happens here instead of as a server error.
 */
export function ApiKeyCreateDialog({ open, submitting, onCreate, onCancel }: ApiKeyCreateDialogProps) {
	const [name, setName] = useState('')
	const [days, setDays] = useState(String(API_KEY_DEFAULT_EXPIRY_DAYS))

	// Reset between openings, so a cancelled draft does not reappear.
	useEffect(() => {
		if (!open) return
		setName('')
		setDays(String(API_KEY_DEFAULT_EXPIRY_DAYS))
	}, [open])

	if (!open) return null

	const parsedDays = Number.parseInt(days, 10)
	const daysValid = Number.isInteger(parsedDays) && parsedDays > 0 && parsedDays <= API_KEY_MAX_EXPIRY_DAYS
	const canSubmit = name.trim().length > 0 && daysValid && !submitting

	function handleSubmit(event: React.FormEvent) {
		event.preventDefault()
		if (!canSubmit) return
		onCreate({ name: name.trim(), expiresInDays: parsedDays })
	}

	return (
		<DialogContent
			size="md"
			isOpen={open}
			onOpenChange={(next) => {
				if (!next) onCancel()
			}}
			isKeyboardDismissDisabled={submitting}
		>
			<form onSubmit={handleSubmit}>
				<DialogTitle>Create an API key</DialogTitle>
				<p className="mt-2 text-ds-sm leading-5 text-ds-text-muted">
					The key acts as you: it can do what you can do, and what it creates is attributed to you.
				</p>

				<div className="mt-4 space-y-4">
					<div>
						<Label htmlFor="api-key-name">Name</Label>
						<Input
							id="api-key-name"
							value={name}
							autoFocus
							maxLength={100}
							placeholder="Figma plugin, laptop, CI"
							onChange={(event) => setName(event.target.value)}
						/>
						<HelpText>What this key is for, so you can tell your keys apart later.</HelpText>
					</div>

					<div>
						<Label htmlFor="api-key-days">Expires in (days)</Label>
						<Input
							id="api-key-days"
							type="number"
							min={1}
							max={API_KEY_MAX_EXPIRY_DAYS}
							value={days}
							onChange={(event) => setDays(event.target.value)}
						/>
						<HelpText>
							{daysValid
								? `Up to ${API_KEY_MAX_EXPIRY_DAYS} days. A key unused for 30 days is blocked automatically.`
								: `Enter a whole number of days between 1 and ${API_KEY_MAX_EXPIRY_DAYS}.`}
						</HelpText>
					</div>
				</div>

				<div className="mt-6 flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						className="rounded-ds-full"
						isDisabled={submitting}
						onPress={onCancel}
					>
						Cancel
					</Button>
					<Button type="submit" variant="primary" className="rounded-ds-full px-5" isDisabled={!canSubmit}>
						{submitting ? 'Creating…' : 'Create key'}
					</Button>
				</div>
			</form>
		</DialogContent>
	)
}
